import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { getCompany, listBrands, listOrders, listProducts, listStores, listUsers, updateCompany } from '../api';
import { imageSpecs } from '../imageSpecs';
import { ImageUploadField } from '../components/ImageUploadField';

export function AdminCompanyDetailPage() {
  const { companyCode = '' } = useParams<{ companyCode: string }>();
  const decodedCompanyCode = decodeURIComponent(companyCode);
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [profile, setProfile] = useState({
    companyName: '',
    legalName: '',
    email: '',
    phoneNumber: '',
    address: '',
    websiteUrl: '',
    publicUrlDecorator: '',
    businessType: '',
    logoUrl: '',
    notes: '',
    assignedUserEmails: '',
    isActive: true,
  });

  const company = useQuery({
    queryKey: ['admin', 'company', decodedCompanyCode],
    queryFn: () => getCompany(decodedCompanyCode),
    enabled: Boolean(decodedCompanyCode),
  });
  const stores = useQuery({
    queryKey: ['admin', 'stores', decodedCompanyCode],
    queryFn: () => listStores(decodedCompanyCode),
    enabled: Boolean(decodedCompanyCode),
  });
  const brands = useQuery({
    queryKey: ['admin', 'brands', decodedCompanyCode],
    queryFn: () => listBrands(decodedCompanyCode),
    enabled: Boolean(decodedCompanyCode),
  });
  const products = useQuery({
    queryKey: ['admin', 'products', decodedCompanyCode],
    queryFn: () => listProducts(decodedCompanyCode),
    enabled: Boolean(decodedCompanyCode),
  });
  const orders = useQuery({
    queryKey: ['admin', 'orders', decodedCompanyCode],
    queryFn: () => listOrders(decodedCompanyCode),
    enabled: Boolean(decodedCompanyCode),
  });
  const team = useQuery({
    queryKey: ['admin', 'company-team', decodedCompanyCode],
    queryFn: () => listUsers(decodedCompanyCode),
    enabled: Boolean(decodedCompanyCode),
  });

  const orderTotal = useMemo(
    () => (orders.data ?? []).reduce((sum, order) => sum + Number(order.totalInr ?? 0), 0),
    [orders.data]
  );

  useEffect(() => {
    if (!company.data) return;
    setProfile({
      companyName: company.data.companyName ?? '',
      legalName: company.data.legalName ?? '',
      email: company.data.email ?? '',
      phoneNumber: company.data.phoneNumber ?? '',
      address: company.data.address ?? '',
      websiteUrl: company.data.websiteUrl ?? '',
      publicUrlDecorator: company.data.publicUrlDecorator ?? '',
      businessType: company.data.businessType ?? '',
      logoUrl: company.data.logoUrl ?? '',
      notes: company.data.notes ?? '',
      assignedUserEmails: company.data.assignedUserEmails?.join(', ') ?? '',
      isActive: company.data.isActive ?? true,
    });
  }, [company.data]);

  const updateMutation = useMutation({
    mutationFn: () => updateCompany(decodedCompanyCode, {
      companyCode: decodedCompanyCode,
      ...profile,
      assignedUserEmails: profile.assignedUserEmails.split(',').map((x) => x.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      setEditOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin', 'company', decodedCompanyCode] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
    },
  });

  if (!decodedCompanyCode) {
    return <EmptyState title="Company not selected" description="Open a company from the Companies screen." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link to="/admin/companies" className="text-sm font-semibold text-brand-700 dark:text-brand-300 hover:underline">
            Back to companies
          </Link>
          <h1 className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
            {company.data?.companyName ?? decodedCompanyCode}
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            Company code <span className="font-mono text-xs">{decodedCompanyCode}</span>
          </p>
        </div>
        {company.data?.isActive === false ? (
          <span className="w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Inactive
          </span>
        ) : (
          <span className="w-fit rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 dark:bg-green-950 dark:text-green-300">
            Active
          </span>
        )}
        <Button variant="secondary" onClick={() => setEditOpen((value) => !value)}>
          {editOpen ? 'Close profile' : 'Edit profile'}
        </Button>
      </div>

      {company.isLoading ? (
        <LoadingSkeleton className="h-28 w-full" />
      ) : company.error ? (
        <Card padded>
          <div className="text-sm text-red-600 dark:text-red-400">{(company.error as Error).message}</div>
        </Card>
      ) : (
        <Card padded>
          <div className="grid gap-4 md:grid-cols-3">
            <Detail label="Legal name" value={company.data?.legalName ?? 'Not configured'} />
            <Detail label="Business type" value={company.data?.businessType ?? 'Not configured'} />
            <Detail label="Public URL" value={`nearrme.com/${company.data?.publicUrlDecorator ?? decodedCompanyCode.toLowerCase()}`} />
            <Detail label="Website" value={company.data?.websiteUrl ?? 'Not configured'} />
            <Detail label="Email" value={company.data?.email ?? 'Not configured'} />
            <Detail label="Phone" value={company.data?.phoneNumber ?? 'Not configured'} />
            <Detail label="Address" value={company.data?.address ?? 'Not configured'} />
            <Detail label="Assigned users" value={company.data?.assignedUserEmails?.join(', ') || 'Not configured'} />
            <Detail label="Logo" value={company.data?.logoUrl ?? 'Not configured'} />
            <Detail label="Notes" value={company.data?.notes ?? 'Not configured'} />
          </div>
        </Card>
      )}

      {editOpen && (
        <Card padded>
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Edit company profile</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <EditField label="Display name" value={profile.companyName} onChange={(value) => setProfile({ ...profile, companyName: value })} />
            <EditField label="Legal name" value={profile.legalName} onChange={(value) => setProfile({ ...profile, legalName: value })} />
            <EditField label="Business type" value={profile.businessType} onChange={(value) => setProfile({ ...profile, businessType: value })} />
            <EditField label="Email" value={profile.email} onChange={(value) => setProfile({ ...profile, email: value })} />
            <EditField label="Phone" value={profile.phoneNumber} onChange={(value) => setProfile({ ...profile, phoneNumber: value })} />
            <EditField label="Website" value={profile.websiteUrl} onChange={(value) => setProfile({ ...profile, websiteUrl: value })} />
            <EditField label="Public URL decorator" value={profile.publicUrlDecorator} onChange={(value) => setProfile({ ...profile, publicUrlDecorator: value })} />
            <ImageUploadField label="Company logo" value={profile.logoUrl} onChange={(value) => setProfile({ ...profile, logoUrl: value })} spec={imageSpecs.companyLogo} />
            <EditField label="Assigned Nearrme users" value={profile.assignedUserEmails} onChange={(value) => setProfile({ ...profile, assignedUserEmails: value })} />
          </div>
          <EditTextArea label="Address" value={profile.address} onChange={(value) => setProfile({ ...profile, address: value })} />
          <EditTextArea label="Notes" value={profile.notes} onChange={(value) => setProfile({ ...profile, notes: value })} />
          <label className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={profile.isActive} onChange={(e) => setProfile({ ...profile, isActive: e.target.checked })} />
            Active company
          </label>
          <div className="mt-4 flex items-center gap-3">
            <Button variant="primary" disabled={updateMutation.isPending} onClick={() => updateMutation.mutate()}>
              Save company profile
            </Button>
            {updateMutation.error && (
              <span className="text-sm text-red-600 dark:text-red-400">{(updateMutation.error as Error).message}</span>
            )}
          </div>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <Metric label="Stores" value={stores.data?.length} loading={stores.isLoading} />
        <Metric label="Brands" value={brands.data?.length} loading={brands.isLoading} />
        <Metric label="Products" value={products.data?.length} loading={products.isLoading} />
        <Metric label="Orders" value={orders.data?.length} loading={orders.isLoading} />
        <Metric label="Order value" value={`Rs. ${orderTotal.toLocaleString('en-IN')}`} loading={orders.isLoading} />
      </div>

      <Section title="Stores">
        {stores.isLoading ? (
          <LoadingSkeleton className="h-32 w-full" />
        ) : stores.data?.length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {stores.data.map((store) => (
              <Card key={store.storeId} padded>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-bold text-slate-950 dark:text-slate-50">{store.name}</h2>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                      {store.productCount} products, {store.availableUnits} available units
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {store.languagesSpoken} | {store.paymentMethods}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-brand-700 dark:text-brand-300">{store.rating.toFixed(1)}</span>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState title="No stores" description="No store locations are linked to this company yet." />
        )}
      </Section>

      <Section title="Brands">
        <CompactGrid
          loading={brands.isLoading}
          emptyTitle="No brands"
          items={(brands.data ?? []).slice(0, 8).map((brand) => ({
            id: String(brand.brandId),
            title: brand.brandName ?? 'Unnamed brand',
            detail: brand.description ?? brand.category ?? 'Manufacturer verified brand',
          }))}
        />
      </Section>

      <Section title="Products">
        <CompactGrid
          loading={products.isLoading}
          emptyTitle="No products"
          items={(products.data ?? []).slice(0, 8).map((product) => ({
            id: String(product.productId),
            title: product.productName ?? 'Unnamed product',
            detail: `${product.brandName ?? 'Brand pending'} | ${product.inStock ? 'In stock' : 'No stock'}`,
          }))}
        />
      </Section>

      <Section title="Orders">
        <CompactGrid
          loading={orders.isLoading}
          emptyTitle="No orders"
          items={(orders.data ?? []).slice(0, 8).map((order) => ({
            id: String(order.consumerOrderId),
            title: order.productName ?? `Order ${order.consumerOrderId}`,
            detail: `${order.fulfilmentMode} | Rs. ${Number(order.totalInr).toLocaleString('en-IN')} | ${order.consumerName}`,
          }))}
        />
      </Section>

      <Section title="Company Team">
        <CompactGrid
          loading={team.isLoading}
          emptyTitle="No team members"
          items={(team.data ?? []).map((member) => ({
            id: String(member.userProfileId),
            title: `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.email || 'Team member',
            detail: `${member.role ?? 'Role pending'} | ${member.email ?? 'Email pending'}`,
          }))}
        />
      </Section>
    </div>
  );
}

function EditField(props: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">{props.label}</span>
      <input
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      />
    </label>
  );
}

function EditTextArea(props: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="mt-3 block">
      <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">{props.label}</span>
      <textarea
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className="min-h-20 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      />
    </label>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

function Metric({ label, value, loading }: { label: string; value?: number | string; loading: boolean }) {
  return (
    <Card padded>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-2 text-2xl font-bold text-slate-950 dark:text-slate-50">{loading ? '-' : value ?? 0}</div>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 text-xl font-bold text-slate-950 dark:text-slate-50">{title}</h2>
      {children}
    </section>
  );
}

function CompactGrid({
  loading,
  emptyTitle,
  items,
}: {
  loading: boolean;
  emptyTitle: string;
  items: Array<{ id: string; title: string; detail: string }>;
}) {
  if (loading) return <LoadingSkeleton className="h-32 w-full" />;
  if (items.length === 0) return <EmptyState title={emptyTitle} />;

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => (
        <Card key={item.id} padded>
          <div className="text-sm font-bold text-slate-950 dark:text-slate-50">{item.title}</div>
          <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">{item.detail}</div>
        </Card>
      ))}
    </div>
  );
}
